import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Form, Input, Modal, Space, Typography } from "antd";
import { ArrowLeftOutlined, ClearOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SignaturePad from "./SignaturePad";
import ReportPreview from "./ReportPreview";

const locate = () =>
  new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: Math.round(position.coords.accuracy)
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 }
    );
  });

/**
 * The customer reads the report, then signs with a finger on the worker's device.
 * Used on site (with the copy and location options) and on the public link page.
 */
export const SignatureForm = ({
  defaultName = "",
  defaultEmail = "",
  showCopy = true,
  showLocation = true,
  submitting,
  onSubmit,
  onBack
}) => {
  const { t } = useTranslation("dashboard");
  const pad = useRef(null);
  const [name, setName] = useState(defaultName);
  const [hasInk, setHasInk] = useState(false);
  const [emailCopy, setEmailCopy] = useState(Boolean(defaultEmail));
  const [email, setEmail] = useState(defaultEmail);
  const [withLocation, setWithLocation] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (name.trim().length < 2) return setError(t("Please enter the name of the person signing."));
    if (!pad.current || pad.current.isEmpty()) return setError(t("Please sign in the box."));
    const place = showLocation && withLocation ? await locate() : null;
    onSubmit({
      name: name.trim(),
      image: pad.current.toDataURL(),
      place,
      emailCopy: showCopy ? emailCopy : undefined,
      email: showCopy && emailCopy ? email.trim() : undefined
    });
  };

  return (
    <Form layout="vertical" className="wr-sign-form" onFinish={submit}>
      <Form.Item label={t("Name of the person signing")} required>
        <Input
          size="large"
          value={name}
          autoComplete="name"
          maxLength={120}
          onChange={event => setName(event.target.value)}
        />
      </Form.Item>
      <div className="wr-signature-box">
        <SignaturePad ref={pad} onChange={setHasInk} ariaLabel={t("Signature")} />
        <span className="wr-signature-hint" aria-hidden="true">
          {hasInk ? "" : t("Sign here")}
        </span>
        <Button
          className="wr-signature-clear"
          size="small"
          icon={<ClearOutlined />}
          onClick={() => pad.current?.clear()}
        >
          {t("Clear")}
        </Button>
      </div>
      {showCopy && (
        <Space direction="vertical" className="wr-full">
          <Checkbox checked={emailCopy} onChange={event => setEmailCopy(event.target.checked)}>
            {t("Send the signed report to the customer by email")}
          </Checkbox>
          {emailCopy && (
            <Input
              type="email"
              inputMode="email"
              value={email}
              placeholder={t("Email address")}
              onChange={event => setEmail(event.target.value)}
            />
          )}
        </Space>
      )}
      {showLocation && (
        <Checkbox
          className="wr-location"
          checked={withLocation}
          onChange={event => setWithLocation(event.target.checked)}
        >
          {t("Record where it was signed (GPS)")}
        </Checkbox>
      )}
      {error && <Alert type="error" showIcon message={error} className="wr-sign-error" />}
      <div className="wr-sign-actions">
        {onBack && (
          <Button size="large" icon={<ArrowLeftOutlined />} onClick={onBack}>
            {t("Back")}
          </Button>
        )}
        <Button
          size="large"
          type="primary"
          htmlType="submit"
          icon={<EditOutlined />}
          loading={submitting}
        >
          {t("Sign")}
        </Button>
      </div>
    </Form>
  );
};

const SignDialog = ({ open, view, photoSrc, defaultName, defaultEmail, onCancel, onSign }) => {
  const { t } = useTranslation("dashboard");
  const [step, setStep] = useState("review");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setStep("review");
  }, [open]);

  const submit = async values => {
    setSubmitting(true);
    try {
      await onSign(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      rootClassName="work-reports-page"
      open={open}
      width={760}
      title={step === "review" ? t("Please check the report") : t("Signature of the customer")}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      className="wr-sign-modal"
      footer={
        step === "review"
          ? [
              <Button key="cancel" onClick={onCancel}>
                {t("Cancel")}
              </Button>,
              <Button key="next" type="primary" onClick={() => setStep("sign")}>
                {t("Continue to signature")}
              </Button>
            ]
          : null
      }
    >
      {step === "review" ? (
        <>
          <Typography.Paragraph type="secondary">
            {t("Hand the device to the customer. They check the work and the times, then sign.")}
          </Typography.Paragraph>
          <ReportPreview view={view} photoSrc={photoSrc} />
        </>
      ) : (
        <SignatureForm
          defaultName={defaultName}
          defaultEmail={defaultEmail}
          submitting={submitting}
          onSubmit={submit}
          onBack={() => setStep("review")}
        />
      )}
    </Modal>
  );
};

export default SignDialog;
